import React, { useEffect, useMemo, useState } from 'react';
import { collection, getDocs, query, where, orderBy } from 'firebase/firestore';
import { db } from '../firebase/firebase';
import ModelCard from './ModelCard';
import './PublicView.css';

const safeDate = (v) => {
  try {
    if (!v) return null;
    if (v instanceof Date) return v;
    if (typeof v?.toDate === 'function') return v.toDate();
    const d = new Date(v);
    return isNaN(d.getTime()) ? null : d;
  } catch {
    return null;
  }
};

const PublicView = () => {
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [filter, setFilter] = useState({ category: '', era: '' });
  const [showFilters, setShowFilters] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    const fetchModels = async () => {
      setLoading(true);
      setError(null);
      try {
        const modelsRef = collection(db, 'models');

        let snap;
        try {
          snap = await getDocs(query(
            modelsRef,
            where('isPublic', '==', true),
            orderBy('createdAt', 'desc')
          ));
        } catch {
          snap = await getDocs(query(
            modelsRef,
            where('isPublic', '==', true)
          ));
        }

        const list = snap.docs.map((d) => {
          const data = d.data() || {};
          return {
            id: d.id,
            name: data.name || 'Sin nombre',
            description: data.description || '',
            category: data.category || '',
            era: data.era || '',
            location: data.location || '',
            locationCoords: data.locationCoords || null,
            thumbnailUrl: data.thumbnailUrl || null,
            modelUrl: data.modelUrl || null,
            createdAt: safeDate(data.createdAt),
            isPublic: typeof data.isPublic === 'boolean' ? data.isPublic : false,
          };
        }).filter((m) => m.isPublic === true);

        if (list.length && list.every((m) => m.createdAt)) {
          list.sort((a, b) => (b.createdAt?.getTime?.() || 0) - (a.createdAt?.getTime?.() || 0));
        }

        setModels(list);
      } catch (e) {
        console.error('Error fetching public models:', e);
        setError('No se pudo cargar la colección pública.');
        setModels([]);
      } finally {
        setLoading(false);
      }
    };

    fetchModels();
  }, []);

  const normalizedQuery = useMemo(() => searchQuery.trim().toLowerCase(), [searchQuery]);

  const filteredModels = useMemo(() => {
    const byText = (m) =>
      (m.name || '').toLowerCase().includes(normalizedQuery) ||
      (m.description || '').toLowerCase().includes(normalizedQuery);

    const byCategory = (m) => !filter.category || m.category === filter.category;
    const byEra = (m) => !filter.era || m.era === filter.era;

    return models.filter((m) => byText(m) && byCategory(m) && byEra(m));
  }, [models, normalizedQuery, filter.category, filter.era]);

  const uniqueCategories = useMemo(
    () => [...new Set(models.map((m) => m.category).filter(Boolean))],
    [models]
  );

  const uniqueEras = useMemo(
    () => [...new Set(models.map((m) => m.era).filter(Boolean))],
    [models]
  );

  const stats = useMemo(() => ([
    { label: 'Modelos Públicos', value: models.length },
    { label: 'Categorías', value: uniqueCategories.length },
    { label: 'Épocas', value: uniqueEras.length }
  ]), [models.length, uniqueCategories.length, uniqueEras.length]);

  const toggleFilters = () => setShowFilters((v) => !v);
  const resetFilters = () => {
    setFilter({ category: '', era: '' });
    setSearchQuery('');
  };

  return (
    <div className="modern-gallery">
      <header className="gallery-header">
        <div className="header-grid-overlay" aria-hidden="true" />
        <div className="header-content">
          <span className="hero-eyebrow">Museo Digital | AR + Datos Geoespaciales</span>
          <h1 className="gallery-title">Aplicación Inteligente de Digitalización</h1>
          <p className="gallery-subtitle">
            Explora patrimonio cultural en 3D con una interfaz de nueva generación para visualizar, filtrar y descubrir modelos en tiempo real.
          </p>

          <div className="hero-stats">
            {stats.map((item) => (
              <div key={item.label} className="hero-stat-card">
                <span className="hero-stat-value">{item.value}</span>
                <span className="hero-stat-label">{item.label}</span>
              </div>
            ))}
          </div>

          <div className="search-filter-container">
            <div className="search-bar">
              <input
                type="text"
                placeholder="Buscar modelos públicos por nombre o descripción..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                aria-label="Buscar modelos"
              />
              <svg className="search-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>

            <button onClick={toggleFilters} className="filter-toggle-button">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
              </svg>
              {showFilters ? 'Ocultar filtros' : 'Filtrar colección'}
            </button>
          </div>
        </div>
      </header>

      {showFilters && (
        <div className="filter-panel">
          <div className="filter-grid">
            <div className="filter-group">
              <label className="filter-label">Categoría</label>
              <div className="filter-select-wrapper">
                <select
                  value={filter.category}
                  onChange={(e) => setFilter((f) => ({ ...f, category: e.target.value }))}
                  className="filter-select"
                >
                  <option value="">Todas las categorías</option>
                  {uniqueCategories.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="filter-group">
              <label className="filter-label">Época</label>
              <div className="filter-select-wrapper">
                <select
                  value={filter.era}
                  onChange={(e) => setFilter((f) => ({ ...f, era: e.target.value }))}
                  className="filter-select"
                >
                  <option value="">Todas las épocas</option>
                  {uniqueEras.map((e) => (
                    <option key={e} value={e}>{e}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className="filter-actions">
            <button onClick={resetFilters} className="reset-filters-btn">Limpiar filtros</button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="loading-container">
          <div className="loading-spinner" />
          <p>Cargando modelos...</p>
        </div>
      ) : error ? (
        <div className="no-models">
          <div className="no-models-icon">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h3 className="no-models-title">Ups...</h3>
          <p className="no-models-text">{error}</p>
        </div>
      ) : filteredModels.length === 0 ? (
        <div className="no-models">
          <div className="no-models-icon">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h3 className="no-models-title">No se encontraron modelos públicos</h3>
          <p className="no-models-text">Ajusta los filtros o vuelve más tarde.</p>
          <button onClick={resetFilters} className="reset-filters-btn">Limpiar todos los filtros</button>
        </div>
      ) : (
        <section className="modern-models-grid" id="coleccion-publica">
          {filteredModels.map((model) => (
            <ModelCard
              key={model.id}
              model={model}
            />
          ))}
        </section>
      )}
    </div>
  );
};

export default PublicView;
